import React from "react"
import throttle from "lodash/throttle"
import { passiveListener } from "src/utils/feature-detect"
import * as wndw from "src/utils/wndw"

const noop = () => {}

const defaultValue = {
  scrolledToTop: true,
  scrolledToBottom: false,
  scrollY: 0,
  hasScrolled: true,
  scrolledUp: false,
  doScroll: noop,
}

const { Provider, Consumer } = React.createContext(defaultValue)

class ScrollListener extends React.Component {
  static Consumer = Consumer

  state = defaultValue

  _cachedBodyHeight = 0
  _cachedViewportHeight = 0

  componentDidMount() {
    this.lastScrollY = typeof window !== "undefined" ? wndw.scrollY() : 0
    this._updateCachedDimensions()
    this._observeIntersection()
    this.observe()
  }

  componentWillUnmount() {
    this._unobserveIntersection()
    this.unobserve()
  }

  _intersectionObserver = null

  _observeIntersection = () => {
    if (typeof window === "undefined" || !window.IntersectionObserver) return
    const sentinel = document.getElementById("pageTop")
    if (!sentinel) return
    this._intersectionObserver = new IntersectionObserver(
      (entries) => {
        const entry = entries[0]
        if (!entry) return
        const rect = entry.boundingClientRect
        const atTop = rect.top >= 0
        if (atTop && !this.state.scrolledToTop) {
          this.setState({ scrolledToTop: true })
        } else if (!atTop && this.state.scrolledToTop) {
          this.setState({ scrolledToTop: false })
        }
      },
      { threshold: [0, 1], rootMargin: "0px" }
    )
    this._intersectionObserver.observe(sentinel)
  }

  _unobserveIntersection = () => {
    if (this._intersectionObserver) {
      const sentinel = document.getElementById("pageTop")
      if (sentinel) this._intersectionObserver.unobserve(sentinel)
      this._intersectionObserver = null
    }
  }

  _updateCachedDimensions = () => {
    this._cachedBodyHeight = document.body.clientHeight
    this._cachedViewportHeight = window.innerHeight
  }

  _scrollHandler = () => {
    const newScrollY = Math.max(0, wndw.scrollY())

    const delta = newScrollY - this.lastScrollY
    this.lastScrollY = newScrollY

    if (this.state.ignoreScroll || isNaN(delta)) {
      return
    }

    const scrolledUp = delta < 0

    if (delta === 0) {
      return
    }

    if (!this.state.hasScrolled && this.lastScrollY > 0) {
      this.setState({ hasScrolled: true })
    }

    const atTop = newScrollY < 1
    if (atTop) {
      if (!this.state.scrolledToTop) {
        this.setState({ scrolledToTop: true })
      }
    } else if (this.state.scrolledToTop) {
      this.setState({ scrolledToTop: false })
    }

    const nearBottom =
      newScrollY >= this._cachedBodyHeight - this._cachedViewportHeight * 1.2
    if (nearBottom && !this.state.scrolledToBottom) {
      this._updateCachedDimensions()
      this.setState({ scrolledToBottom: true })
    } else if (!nearBottom && this.state.scrolledToBottom) {
      this.setState({ scrolledToBottom: false })
    }

    if (this.state.scrolledUp !== scrolledUp) {
      this.setState({ scrolledUp: scrolledUp })
    }
  }

  scrollHandler = throttle(this._scrollHandler, 100)

  observe = () => {
    window.addEventListener(
      "scroll",
      this.scrollHandler,
      passiveListener() ? { passive: true } : false
    )
    window.addEventListener("resize", this._updateCachedDimensions)
  }

  unobserve = () => {
    window.removeEventListener(
      "scroll",
      this.scrollHandler,
      passiveListener() ? { passive: true } : false
    )
    window.removeEventListener("resize", this._updateCachedDimensions)
  }

  doScroll = (targetY, duration = 500) => {
    return new Promise((resolve) => {
      // Tell scroll listener to ignore until we're done scrolling
      this.setState(
        {
          ignoreScroll: true,
        },
        () => {
          // ignore flag set, now start scrolling
          wndw.scrollTo(targetY, duration).then(() => {
            resolve()
            window.setTimeout(() => {
              // scroll complete
              // use timeout to add buffer before re-enabling scroll listening
              // to handle timing edge cases
              this.setState({
                ignoreScroll: false,
              })
            }, 100)
          })
        }
      )
    })
  }

  render() {
    const {
      scrolledToTop,
      scrolledToBottom,
      scrollY,
      scrolledUp,
      hasScrolled,
    } = this.state

    const { children } = this.props

    return (
      <Provider
        value={{
          scrolledToTop,
          scrolledToBottom,
          scrollY,
          scrolledUp,
          hasScrolled,
          doScroll: this.doScroll,
        }}
      >
        {children}
      </Provider>
    )
  }
}

export default ScrollListener
